import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

interface ImpairmentEvent {
  id: number;
  situationId: number;
  transitionedFromStatus: string | null;
  transitionedToStatus: string;
  assetSilo: string;
  assetSiloId: number;
  assetDisplayName: string;
  portfolioId: number | null;
  carryingValueBefore: number | null;
  fairValueAfter: number | null;
  unrealizedGainLoss: number | null;
  marketPricePct: number | null;
  nonAccrualFlipped: boolean;
  nonAccrualDate: string | null;
  iborJournalEntries: string | null;
  triggeredBy: string | null;
  triggeredAt: string;
  notes: string | null;
}

interface Claim {
  type: string;
  amount: number;
  recoveryEstimate: number;
}

interface Situation {
  id: number;
  companyName: string;
  ticker?: string;
  industry?: string;
  situationType: string;
  status: string;
  filingDate?: string;
  courtName?: string;
  caseNumber?: string;
  totalDebt?: number;
  ourExposure?: number;
  estimatedRecovery?: number;
  nextDeadline?: string;
  deadlineDescription?: string;
  dipFinancing?: boolean;
  dipAmount?: number;
  claims?: Claim[];
}

interface RecoveryAnalysis {
  totalFaceAmount?: number;
  totalEstimatedRecovery?: number;
  weightedRecoveryRate?: number;
  iborExposure?: number;
  approvedScenarioCount?: number;
  approvedScenarioRecoveryPct?: number;
}

const STATUSES = [
  'MONITORING',
  'ACTIVE',
  'PLAN_CONFIRMATION',
  'PLAN_CONFIRMED',
  'LIQUIDATING',
  'RESTRUCTURED',
  'EMERGED',
  'DISMISSED',
  'CONVERTED',
];

const IMPAIRED_SET = new Set(['ACTIVE', 'PLAN_CONFIRMATION', 'LIQUIDATING', 'RESTRUCTURED']);

interface Props {
  embedded?: boolean;
  idOverride?: string;
}

const DistressedSituationDetailScreen: React.FC<Props> = ({ embedded = false, idOverride }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = idOverride ?? searchParams.get('id') ?? '';
  const [situation, setSituation] = useState<Situation | null>(null);
  const [trail, setTrail] = useState<ImpairmentEvent[]>([]);
  const [recovery, setRecovery] = useState<RecoveryAnalysis | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'claims' | 'trail'>('overview');
  const [nextStatus, setNextStatus] = useState<string>('');
  const [marketPricePct, setMarketPricePct] = useState('');
  const [valuationSource, setValuationSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [sRes, tRes, rRes] = await Promise.all([
        axios.get(`/api/credit/distressed/${id}`),
        axios.get(`/api/credit/distressed/${id}/impairment-trail`),
        axios.get(`/api/credit/distressed/${id}/recovery`).catch(() => ({ data: null })),
      ]);
      setSituation(sRes.data);
      setTrail(Array.isArray(tRes.data) ? tRes.data : []);
      setRecovery(rRes.data);
      setNextStatus(sRes.data?.status ?? '');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load situation');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleApplyStatus = async () => {
    if (!id || !nextStatus || nextStatus === situation?.status) return;
    try {
      setBusy(true);
      setError(null);
      const transitionEvidence = IMPAIRED_SET.has(nextStatus) && !IMPAIRED_SET.has(situation?.status ?? '')
        ? { marketPricePct: Number(marketPricePct), valuationSource: valuationSource.trim() }
        : {};
      await axios.put(`/api/credit/distressed/${id}/status`, {
        status: nextStatus,
        ...transitionEvidence,
      });
      setFlash(
        IMPAIRED_SET.has(nextStatus) && !IMPAIRED_SET.has(situation?.status ?? '')
          ? `Status changed to ${nextStatus}. The evidenced ${Number(marketPricePct).toFixed(2)}% mark was posted, non-accrual was evaluated, and the audit journal was persisted.`
          : `Status updated to ${nextStatus}.`
      );
      await fetchAll();
      if (IMPAIRED_SET.has(nextStatus) && !IMPAIRED_SET.has(situation?.status ?? '')) {
        setTimeout(() => setActiveTab('trail'), 350);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Status update failed');
    } finally {
      setBusy(false);
      setTimeout(() => setFlash(null), 8000);
    }
  };

  const goBack = () => {
    const params = new URLSearchParams(searchParams);
    params.set('sub', 'distressed');
    params.delete('id');
    setSearchParams(params, { replace: true });
  };

  if (!id) return <Empty>No situation id provided.</Empty>;
  if (loading) return <Empty>Loading…</Empty>;
  if (!situation) return <Empty>{error ?? 'Situation not found.'}</Empty>;

  const willImpair = IMPAIRED_SET.has(nextStatus) && !IMPAIRED_SET.has(situation.status);
  const statusChanged = nextStatus && nextStatus !== situation.status;
  const headerExposure = recovery?.iborExposure ?? situation.ourExposure;
  const headerTotalDebt = recovery?.totalFaceAmount && recovery.totalFaceAmount > 0
    ? recovery.totalFaceAmount
    : situation.totalDebt;
  const headerRecoveryPct = recovery?.approvedScenarioRecoveryPct
    ?? recovery?.weightedRecoveryRate
    ?? situation.estimatedRecovery
    ?? 0;

  return (
    <Container $embedded={embedded}>
      <Breadcrumb>
        <BackLink onClick={goBack}>← Distressed Situations</BackLink>
        <Sep>/</Sep>
        <Crumb>{situation.companyName}</Crumb>
      </Breadcrumb>

      <Header>
        <HeaderLeft>
          <Company>{situation.companyName}</Company>
          <Meta>
            {situation.ticker && <Chip>{situation.ticker}</Chip>}
            <TypeBadge $type={situation.situationType}>{(situation.situationType || '').replace(/_/g, ' ')}</TypeBadge>
            <StatusBadge $status={situation.status}>{(situation.status || '').replace(/_/g, ' ')}</StatusBadge>
            {situation.industry && <Muted>{situation.industry}</Muted>}
          </Meta>
        </HeaderLeft>
        <HeaderRight>
          <StatBlock>
            <StatLabel>Our Exposure</StatLabel>
            <StatVal>{fmtCurrency(headerExposure)}</StatVal>
          </StatBlock>
          <StatBlock>
            <StatLabel>Total Debt</StatLabel>
            <StatVal>{fmtCurrency(headerTotalDebt)}</StatVal>
          </StatBlock>
          <StatBlock>
            <StatLabel>Est. Recovery</StatLabel>
            <StatVal>{Number(headerRecoveryPct).toFixed(2)}%</StatVal>
          </StatBlock>
        </HeaderRight>
      </Header>

      {error && <ErrorBanner>{error}</ErrorBanner>}
      {flash && <SuccessBanner>{flash}</SuccessBanner>}

      <Tabs>
        <Tab $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>Overview</Tab>
        <Tab $active={activeTab === 'claims'} onClick={() => setActiveTab('claims')}>
          Claims {situation.claims?.length ? <Pill>{situation.claims.length}</Pill> : null}
        </Tab>
        <Tab $active={activeTab === 'trail'} onClick={() => setActiveTab('trail')}>
          Impairment Trail {trail.length > 0 ? <Pill>{trail.length}</Pill> : null}
        </Tab>
      </Tabs>

      {activeTab === 'overview' && (
        <Panel>
          <SectionTitle>Status Transition</SectionTitle>
          <Row>
            <Field>
              <FieldLabel>Current</FieldLabel>
              <StatusBadge $status={situation.status} style={{ alignSelf: 'flex-start' }}>
                {situation.status.replace(/_/g, ' ')}
              </StatusBadge>
            </Field>
            <Arrow>→</Arrow>
            <Field>
              <FieldLabel>Move to</FieldLabel>
              <Select value={nextStatus} onChange={e => setNextStatus(e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </Select>
              {willImpair && (
                <>
                  <Hint>
                    Impairment requires an evidenced market mark. The backend will derive fair value
                    from the entered price; it will not invent a recovery haircut.
                  </Hint>
                  <FieldLabel>Market price (% of par)</FieldLabel>
                  <Input
                    type="number"
                    min="0.0001"
                    max="100"
                    step="0.01"
                    value={marketPricePct}
                    onChange={e => setMarketPricePct(e.target.value)}
                    placeholder="e.g. 72.50"
                  />
                  <FieldLabel>Valuation source</FieldLabel>
                  <Input
                    value={valuationSource}
                    onChange={e => setValuationSource(e.target.value)}
                    placeholder="Agent quote, valuation report…"
                  />
                </>
              )}
            </Field>
            <ApplyBtn
              disabled={busy || !statusChanged || (willImpair
                && (!(Number(marketPricePct) > 0 && Number(marketPricePct) <= 100)
                  || !valuationSource.trim()))}
              onClick={handleApplyStatus}
            >
              {busy ? 'Applying…' : 'Apply'}
            </ApplyBtn>
          </Row>

          <SectionTitle>Filing & Court</SectionTitle>
          <Grid>
            <Cell><CellLabel>Filing Date</CellLabel><CellVal>{fmtDate(situation.filingDate)}</CellVal></Cell>
            <Cell><CellLabel>Court</CellLabel><CellVal>{situation.courtName ?? '—'}</CellVal></Cell>
            <Cell><CellLabel>Case Number</CellLabel><CellVal>{situation.caseNumber ?? '—'}</CellVal></Cell>
            <Cell><CellLabel>Next Deadline</CellLabel><CellVal>{fmtDate(situation.nextDeadline)}</CellVal></Cell>
          </Grid>
          {situation.deadlineDescription && (
            <DeadlineNote>{situation.deadlineDescription}</DeadlineNote>
          )}

          {situation.dipFinancing && (
            <>
              <SectionTitle>DIP Financing</SectionTitle>
              <Grid>
                <Cell><CellLabel>DIP Amount</CellLabel><CellVal>{fmtCurrency(situation.dipAmount)}</CellVal></Cell>
              </Grid>
            </>
          )}
        </Panel>
      )}

      {activeTab === 'claims' && (
        <Panel>
          {(situation.claims ?? []).length === 0 ? (
            <Empty>No claim breakdown recorded.</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Class</Th>
                  <Th $right>Claim Amount</Th>
                  <Th $right>Recovery %</Th>
                  <Th $right>Expected Recovery</Th>
                </tr>
              </thead>
              <tbody>
                {(situation.claims ?? []).map((c, i) => (
                  <tr key={i}>
                    <Td><ClaimBadge $type={c.type}>{(c.type || '').replace(/_/g, ' ')}</ClaimBadge></Td>
                    <Td $right>{fmtCurrency(c.amount)}</Td>
                    <Td $right>{c.recoveryEstimate ?? 0}%</Td>
                    <Td $right>{fmtCurrency((c.amount || 0) * ((c.recoveryEstimate || 0) / 100))}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Panel>
      )}

      {activeTab === 'trail' && (
        <Panel>
          {trail.length === 0 ? (
            <Empty>
              No impairment events yet. Move this situation into an impaired state
              (PLAN_CONFIRMATION, ACTIVE, LIQUIDATING, RESTRUCTURED) from the Overview tab.
            </Empty>
          ) : (
            <TrailList>
              {trail.map(ev => (
                <TrailCard key={ev.id}>
                  <TrailHead>
                    <TrailWhen>{fmtDateTime(ev.triggeredAt)}</TrailWhen>
                    <TrailTrans>
                      <Mono>{ev.transitionedFromStatus ?? '—'}</Mono>
                      <Arrow>→</Arrow>
                      <Mono>{ev.transitionedToStatus}</Mono>
                    </TrailTrans>
                    <SiloBadge $silo={ev.assetSilo}>{ev.assetSilo.replace(/_/g, ' ')}</SiloBadge>
                  </TrailHead>
                  <TrailAsset>{ev.assetDisplayName}</TrailAsset>
                  <TrailMetrics>
                    <Metric>
                      <MetricLabel>Carrying Value (Before)</MetricLabel>
                      <MetricVal>{fmtCurrency(ev.carryingValueBefore)}</MetricVal>
                    </Metric>
                    <Metric>
                      <MetricLabel>Fair Value (After)</MetricLabel>
                      <MetricVal>{fmtCurrency(ev.fairValueAfter)}</MetricVal>
                    </Metric>
                    <Metric>
                      <MetricLabel>Unrealized G/L</MetricLabel>
                      <MetricVal $loss={(ev.unrealizedGainLoss ?? 0) < 0}>{fmtCurrency(ev.unrealizedGainLoss)}</MetricVal>
                    </Metric>
                    <Metric>
                      <MetricLabel>Mark Price</MetricLabel>
                      <MetricVal>{ev.marketPricePct != null ? `${Number(ev.marketPricePct).toFixed(2)}%` : '—'}</MetricVal>
                    </Metric>
                    <Metric>
                      <MetricLabel>Non-Accrual</MetricLabel>
                      <MetricVal>
                        {ev.nonAccrualFlipped ? `✓ Flipped ${fmtDate(ev.nonAccrualDate)}` : '— No change'}
                      </MetricVal>
                    </Metric>
                    <Metric>
                      <MetricLabel>Triggered By</MetricLabel>
                      <MetricVal>{ev.triggeredBy ?? 'system'}</MetricVal>
                    </Metric>
                  </TrailMetrics>
                  {ev.iborJournalEntries && (
                    <JournalSection>
                      <JournalLabel>IBOR Journal Entries</JournalLabel>
                      <JournalBody>{prettyJson(ev.iborJournalEntries)}</JournalBody>
                    </JournalSection>
                  )}
                  {ev.notes && <Notes>{ev.notes}</Notes>}
                </TrailCard>
              ))}
            </TrailList>
          )}
        </Panel>
      )}
    </Container>
  );
};

// ---------- helpers ----------

const fmtCurrency = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(2)}`;
};

const fmtDate = (s: string | null | undefined): string => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
};

const fmtDateTime = (s: string): string => {
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

const prettyJson = (raw: string): string => {
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
};

// ---------- styled ----------

const Container = styled.div<{ $embedded?: boolean }>`
  max-width: 1800px;
  margin: 0 auto;
  padding: ${p => p.$embedded ? '1rem' : '2rem'};
  color: white;
`;

const Breadcrumb = styled.div`
  display: flex; align-items: center; gap: 0.5rem;
  margin-bottom: 1rem; font-size: 0.85rem; color: #94a3b8;
`;
const BackLink = styled.button`
  background: none; border: none; color: #60a5fa; cursor: pointer; font-size: 0.85rem;
  padding: 0; &:hover { color: #93c5fd; text-decoration: underline; }
`;
const Sep = styled.span` color: #475569; `;
const Crumb = styled.span` color: #cbd5e1; `;

const Header = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 2rem; padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255,255,255,0.08); margin-bottom: 1.5rem;
`;
const HeaderLeft = styled.div` display: flex; flex-direction: column; gap: 0.5rem; `;
const HeaderRight = styled.div` display: flex; gap: 1.5rem; `;
const Company = styled.h1` font-size: 1.5rem; font-weight: 700; margin: 0; `;
const Meta = styled.div` display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; `;
const Chip = styled.span`
  padding: 2px 8px; background: rgba(148,163,184,0.15); color: #cbd5e1;
  border-radius: 4px; font-size: 0.75rem; font-weight: 600;
`;
const Muted = styled.span` color: #64748b; font-size: 0.8rem; `;

const StatBlock = styled.div` display: flex; flex-direction: column; align-items: flex-end; `;
const StatLabel = styled.div`
  color: #64748b; font-size: 0.7rem; text-transform: uppercase;
  letter-spacing: 0.05em; margin-bottom: 4px;
`;
const StatVal = styled.div` color: white; font-size: 1.3rem; font-weight: 700; font-variant-numeric: tabular-nums; `;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem; margin-bottom: 1rem;
  background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3);
  border-radius: 8px; color: #ef4444; font-size: 0.875rem;
`;
const SuccessBanner = styled.div`
  padding: 0.75rem 1rem; margin-bottom: 1rem;
  background: rgba(16,185,129,0.1); border: 1px solid rgba(16,185,129,0.3);
  border-radius: 8px; color: #10b981; font-size: 0.875rem;
`;

const Tabs = styled.div`
  display: flex; gap: 0.25rem; border-bottom: 1px solid rgba(255,255,255,0.08);
  margin-bottom: 1.5rem;
`;
const Tab = styled.button<{ $active: boolean }>`
  padding: 0.75rem 1.25rem; background: none; border: none;
  color: ${p => p.$active ? 'white' : '#94a3b8'};
  font-weight: ${p => p.$active ? 700 : 500}; font-size: 0.9rem;
  cursor: pointer; border-bottom: 2px solid ${p => p.$active ? '#60a5fa' : 'transparent'};
  display: flex; align-items: center; gap: 0.5rem;
  &:hover { color: white; }
`;
const Pill = styled.span`
  background: rgba(96,165,250,0.2); color: #60a5fa;
  padding: 1px 8px; border-radius: 10px; font-size: 0.7rem; font-weight: 700;
`;

const Panel = styled.div`
  background: rgba(30,41,59,0.5); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px; padding: 1.5rem;
`;
const SectionTitle = styled.div`
  color: #cbd5e1; font-size: 0.8rem; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.05em; margin: 1rem 0 0.75rem; &:first-child { margin-top: 0; }
`;
const Row = styled.div` display: flex; gap: 1rem; align-items: flex-end; flex-wrap: wrap; `;
const Field = styled.div` display: flex; flex-direction: column; gap: 0.5rem; min-width: 180px; `;
const FieldLabel = styled.div` color: #64748b; font-size: 0.75rem; text-transform: uppercase; `;
const Select = styled.select`
  background: rgba(15,23,42,0.8); color: white;
  border: 1px solid rgba(255,255,255,0.15); border-radius: 6px;
  padding: 0.6rem 0.75rem; font-size: 0.9rem; min-width: 200px;
`;
const Input = styled.input`
  background: rgba(15,23,42,0.8); color: white;
  border: 1px solid rgba(255,255,255,0.15); border-radius: 6px;
  padding: 0.6rem 0.75rem; font-size: 0.9rem; min-width: 200px;
`;
const Hint = styled.div`
  color: #fbbf24; font-size: 0.75rem; max-width: 360px; line-height: 1.4;
`;
const Arrow = styled.span` color: #64748b; font-size: 1.2rem; padding: 0 0.5rem; `;
const ApplyBtn = styled.button`
  padding: 0.6rem 1.5rem; border-radius: 6px; font-weight: 600;
  background: linear-gradient(135deg, #ef4444, #dc2626); color: white;
  border: none; cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:not(:disabled):hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(239,68,68,0.4); }
`;

const Grid = styled.div` display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem; `;
const Cell = styled.div``;
const CellLabel = styled.div` color: #64748b; font-size: 0.7rem; text-transform: uppercase; margin-bottom: 4px; `;
const CellVal = styled.div` color: white; font-size: 0.95rem; font-weight: 500; font-variant-numeric: tabular-nums; `;
const DeadlineNote = styled.div` margin-top: 0.5rem; color: #94a3b8; font-size: 0.85rem; font-style: italic; `;

const Table = styled.table` width: 100%; border-collapse: collapse; `;
const Th = styled.th<{ $right?: boolean }>`
  text-align: ${p => p.$right ? 'right' : 'left'}; padding: 0.5rem 0.75rem;
  color: #94a3b8; font-size: 0.75rem; text-transform: uppercase;
  border-bottom: 1px solid rgba(255,255,255,0.1); font-weight: 600;
`;
const Td = styled.td<{ $right?: boolean }>`
  text-align: ${p => p.$right ? 'right' : 'left'}; padding: 0.75rem;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  font-variant-numeric: tabular-nums;
`;
const ClaimBadge = styled.span<{ $type: string }>`
  padding: 3px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600;
  background: ${p => p.$type === 'SECURED' ? 'rgba(16,185,129,0.15)'
    : p.$type === 'SENIOR_UNSECURED' ? 'rgba(59,130,246,0.15)'
    : p.$type === 'SUBORDINATED' ? 'rgba(139,92,246,0.15)' : 'rgba(245,158,11,0.15)'};
  color: ${p => p.$type === 'SECURED' ? '#10b981'
    : p.$type === 'SENIOR_UNSECURED' ? '#3b82f6'
    : p.$type === 'SUBORDINATED' ? '#8b5cf6' : '#f59e0b'};
`;

const TrailList = styled.div` display: flex; flex-direction: column; gap: 1rem; `;
const TrailCard = styled.div`
  background: rgba(15,23,42,0.6); border: 1px solid rgba(255,255,255,0.08);
  border-left: 3px solid #f59e0b;
  border-radius: 8px; padding: 1.25rem;
`;
const TrailHead = styled.div` display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; margin-bottom: 0.5rem; `;
const TrailWhen = styled.div` color: #94a3b8; font-size: 0.8rem; font-variant-numeric: tabular-nums; `;
const TrailTrans = styled.div` display: flex; align-items: center; gap: 0.25rem; color: #cbd5e1; font-size: 0.85rem; `;
const Mono = styled.span` font-family: 'SF Mono', Menlo, monospace; font-size: 0.8rem; padding: 2px 6px; background: rgba(255,255,255,0.05); border-radius: 4px; `;
const SiloBadge = styled.span<{ $silo: string }>`
  margin-left: auto; padding: 3px 8px; border-radius: 4px;
  font-size: 0.7rem; font-weight: 600;
  background: ${p => p.$silo === 'PRIVATE_CREDIT' ? 'rgba(139,92,246,0.15)'
    : p.$silo === 'LEVERAGED_LOAN' ? 'rgba(59,130,246,0.15)' : 'rgba(148,163,184,0.15)'};
  color: ${p => p.$silo === 'PRIVATE_CREDIT' ? '#a78bfa'
    : p.$silo === 'LEVERAGED_LOAN' ? '#60a5fa' : '#94a3b8'};
`;
const TrailAsset = styled.div` color: white; font-weight: 600; margin-bottom: 0.75rem; `;
const TrailMetrics = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem; padding: 0.75rem; background: rgba(0,0,0,0.2); border-radius: 6px;
`;
const Metric = styled.div``;
const MetricLabel = styled.div` color: #64748b; font-size: 0.7rem; text-transform: uppercase; margin-bottom: 2px; `;
const MetricVal = styled.div<{ $loss?: boolean }>`
  color: ${p => p.$loss ? '#ef4444' : 'white'};
  font-size: 0.9rem; font-weight: 600; font-variant-numeric: tabular-nums;
`;
const JournalSection = styled.div` margin-top: 0.75rem; `;
const JournalLabel = styled.div` color: #94a3b8; font-size: 0.75rem; text-transform: uppercase; margin-bottom: 0.5rem; font-weight: 600; `;
const JournalBody = styled.pre`
  background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.05);
  border-radius: 6px; padding: 0.75rem; margin: 0;
  font-family: 'SF Mono', Menlo, monospace; font-size: 0.75rem; color: #cbd5e1;
  overflow-x: auto; max-height: 220px;
`;
const Notes = styled.div` margin-top: 0.75rem; color: #94a3b8; font-size: 0.8rem; font-style: italic; `;

const Empty = styled.div`
  text-align: center; padding: 3rem 1rem; color: #64748b; font-size: 0.9rem;
`;

const TypeBadge = styled.span<{ $type: string }>`
  padding: 3px 10px; border-radius: 4px; font-size: 0.7rem; font-weight: 700;
  text-transform: uppercase;
  background: ${p => p.$type === 'CHAPTER_11' ? 'rgba(239,68,68,0.15)'
    : p.$type === 'CHAPTER_7' ? 'rgba(239,68,68,0.25)'
    : p.$type === 'OUT_OF_COURT' ? 'rgba(245,158,11,0.15)'
    : 'rgba(148,163,184,0.15)'};
  color: ${p => p.$type === 'CHAPTER_11' ? '#ef4444'
    : p.$type === 'CHAPTER_7' ? '#f87171'
    : p.$type === 'OUT_OF_COURT' ? '#f59e0b'
    : '#94a3b8'};
`;
const StatusBadge = styled.span<{ $status: string }>`
  padding: 3px 10px; border-radius: 4px; font-size: 0.7rem; font-weight: 700;
  text-transform: uppercase;
  background: ${p => IMPAIRED_SET.has(p.$status) ? 'rgba(239,68,68,0.15)'
    : p.$status === 'MONITORING' ? 'rgba(245,158,11,0.15)'
    : 'rgba(148,163,184,0.15)'};
  color: ${p => IMPAIRED_SET.has(p.$status) ? '#ef4444'
    : p.$status === 'MONITORING' ? '#f59e0b'
    : '#94a3b8'};
`;

export default DistressedSituationDetailScreen;
